// Lib
import { keyBy, get, negate, isNull, clamp } from 'lodash/fp';

// Utils
import { prop, toArray } from '../../../../../../common/utils/immutableHelper';
import { isLine } from '../../../../../../common/elements/utils/elementTypeUtils';
import {
    getCanvasOriginCoordinates,
    getElementId,
    getElementLocation,
    getIsLineSnapped,
    getXPosition,
    getYPosition,
} from '../../../../../../common/elements/utils/elementPropertyUtils';
import { pixelsToGridPoints } from '../../../../../utils/grid/gridUtils';
import { getLeft, getTop } from '../../../../../../common/maths/geometry/rect';
import { getAdjustedElementMeasurement } from '../../../../../canvas/dnd/canvasUtil';
import { isIconViewLike } from '../../../../../../common/elements/utils/elementDisplayUtils';

// Constants
import { BOARD_ALIGNMENT_OFFSET } from './alignmentTools/alignmentConstants';
import { ImMNElement, MNElementLocation } from '../../../../../../common/elements/elementModelTypes';
import {
    ImElementMeasurement,
    ElementMeasurement,
} from '../../../../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';

const clampPositive = clamp(0, Infinity);

const isArrangeable = (element: unknown): boolean => !(isLine(element) && getIsLineSnapped(element));

type BuildMovesParams = {
    elements: Immutable.Iterable<number, ImMNElement>;
    measurements: ElementMeasurement[];
    gridSize: number;
    currentBoard: ImMNElement;
};

type ElementMoves = { id: string; location: MNElementLocation }[];

const buildMovesForRects = ({ elements, measurements, gridSize, currentBoard }: BuildMovesParams): ElementMoves => {
    // Key by their IDs
    const updatedElementRects = keyBy('id', measurements);

    // Map distributed rects to moves
    return elements
        .map((element) => {
            if (!element) return null;

            const elementId = getElementId(element);
            const location: MNElementLocation = getElementLocation(element).toJS();

            const updatedPosition = updatedElementRects[elementId];

            if (!updatedPosition) return null;

            const canvasOrigin = getCanvasOriginCoordinates(currentBoard);
            const canvasOriginX = prop('x', canvasOrigin) || 0;
            const canvasOriginY = prop('y', canvasOrigin) || 0;

            const x = pixelsToGridPoints(getLeft(updatedPosition), gridSize) + canvasOriginX;
            const y = pixelsToGridPoints(getTop(updatedPosition), gridSize) + canvasOriginY;

            if (x === getXPosition(element) && y === getYPosition(element)) return null;

            return {
                id: elementId,
                location: {
                    ...location,
                    position: {
                        ...get('position', location),
                        x,
                        y,
                    },
                },
            };
        })
        .filter(negate(isNull))
        .toJS();
};

type GetElementMeasurementArrayParams = {
    elements: Immutable.Iterable<number, ImMNElement>;
    measurements: Immutable.Map<string, ImElementMeasurement>;
    gridSize: number;
};

const getElementMeasurementArray = ({
    measurements,
    elements,
    gridSize,
}: GetElementMeasurementArrayParams): ElementMeasurement[] =>
    toArray(elements.map(getAdjustedElementMeasurement(measurements, gridSize))) || [];

const createRemoveBoardOffsetFn = (gridSize: number) => (val: number) =>
    clampPositive(val - BOARD_ALIGNMENT_OFFSET * gridSize);

type ShiftRectsForMovesParams = {
    elements: Immutable.Iterable<number, ImMNElement>;
    measurements: ElementMeasurement[];
    gridSize: number;
};
/**
 * Boards have extra padding on them that are not included in their rects.
 * So in order to position them correctly we need to remove this padding before performing the move.
 */
const shiftRectsForMoves = ({ measurements, elements, gridSize }: ShiftRectsForMovesParams): ElementMeasurement[] => {
    const rectsMap = keyBy('id', measurements);

    return elements
        .map((element) => {
            const elementId = getElementId(element);
            const rect = rectsMap[elementId];

            if (!rect) return null;

            if (isIconViewLike(element)) {
                const removeBoardOffset = createRemoveBoardOffsetFn(gridSize);

                return {
                    ...rect,
                    x: removeBoardOffset(rect.x),
                    y: removeBoardOffset(rect.y),
                    left: removeBoardOffset(rect.left),
                    right: removeBoardOffset(rect.right),
                    top: removeBoardOffset(rect.top),
                    bottom: removeBoardOffset(rect.bottom),
                };
            }

            return rect;
        })
        .filter(negate(isNull))
        .toArray() as ElementMeasurement[];
};

interface GetLayoutActionParams {
    measurements: Immutable.Map<string, ImElementMeasurement>;
    elements: Immutable.Iterable<number, ImMNElement>;
    currentBoard: ImMNElement;
    gridSize: number;
}

export type GetLayoutActionFn = (props: GetLayoutActionParams) => ElementMoves;

export type LayoutCalculatorFn = (measurements: ElementMeasurement[], gridSize: number) => ElementMeasurement[];

export const createLayoutAction = (layoutCalculator: LayoutCalculatorFn): GetLayoutActionFn => {
    return ({ measurements, elements, currentBoard, gridSize }) => {
        // Filter elements that shouldn't be distributed
        const elementsToDistribute = elements.filter(isArrangeable);

        // Get their measurements
        // Add their IDs to the measurements
        const relevantMeasurementRects = getElementMeasurementArray({
            elements: elementsToDistribute,
            measurements,
            gridSize,
        });

        // Get their rects for measurements
        const distributedRects = layoutCalculator(relevantMeasurementRects, gridSize);

        const correctedAlignedRects = shiftRectsForMoves({
            measurements: distributedRects,
            elements: elementsToDistribute,
            gridSize,
        });

        return buildMovesForRects({
            elements: elementsToDistribute,
            measurements: correctedAlignedRects,
            gridSize,
            currentBoard,
        });
    };
};
